import { z } from 'zod'
import { findHealthClaims } from './health-claims'
import { PRICE_BASE_UNITS, unitFactor, type PriceBaseUnit } from '@repo/food-engine'
import { MARKETS, type Currency, type Language, type Market } from './schemas/simulation'
import { AXES, type Axis } from './taxonomy'

/**
 * B2B 광고 (대표 결정 4, 2026-10-30). 소비자 상품 광고가 아니라 사용자 제품의 원료·포장·공장·가게 운영을 파는 회사의 광고다.
 * 원칙: '광고' 표기는 고정, AI 계산·대체안 순서·받은 견적 순서와 섞지 않는다(광고는 결과 데이터에 넣지 않고 따로 받는다),
 * 직접 판매(주문·결제·배송)는 하지 않는다, 실존 회사는 계약을 확인한 관리자 등록 데이터로만 나간다(시드 없음).
 * 광고를 고르는 규칙은 이 파일의 pickAds 하나뿐이다 — 서버가 부르고 시험이 부른다.
 */

/**
 * 광고 자리.
 * - ingredient_swap: 결과 배합표 재료 줄 밑 원료 대체 카드(대기업 원료·업소용 식재료 모두). 같은 공통 식재료끼리만
 * - packaging: 원가 '포장' 줄 밑 용기·포장재 카드
 * - factory: 견적 비교 화면 받은 견적 아래 '추천 공장' 칸
 * - generation_wait: 생성 대기 화면 아래 한 칸 (식품 축 = 원료·포장, 메뉴 축 = 식자재 유통·주방기기·배달 용기·POS)
 * - menu_supply: 메뉴 결과 재료 줄 밑 '업소용 받는 곳' 카드 (신메뉴 식자재 2단계)
 */
export const AD_PLACEMENTS = [
  'ingredient_swap',
  'packaging',
  'factory',
  'generation_wait',
  'menu_supply',
] as const
export type AdPlacement = (typeof AD_PLACEMENTS)[number]

/** 재료 줄에 붙는 자리 — 공통 식재료 키가 있어야 하고, 고정한 재료에는 붙지 않는다 */
export const INGREDIENT_AD_PLACEMENTS: readonly AdPlacement[] = ['ingredient_swap', 'menu_supply']

/** 광고하는 것의 종류. 카드 위 작은 이름표와 관리자 표에 쓴다 */
export const AD_OFFER_KINDS = [
  'ingredient',
  'packaging',
  'factory',
  'food_distribution',
  'kitchen_equipment',
  'delivery_packaging',
  'pos',
  'menu_design',
] as const
export type AdOfferKind = (typeof AD_OFFER_KINDS)[number]

/** 과금 단위. 금액은 관리자가 광고마다 정한다 (대표 결정: 관리자 설정) */
export const AD_BILLING_UNITS = ['per_lead', 'per_click', 'per_mille', 'fixed_slot'] as const
export type AdBillingUnit = (typeof AD_BILLING_UNITS)[number]

/** 광고 카드 상태. approved 이고 기간 안이고 예산이 남았을 때만 나간다 */
export const AD_STATUSES = ['draft', 'pending_review', 'approved', 'paused', 'rejected'] as const
export type AdStatus = (typeof AD_STATUSES)[number]

export const AD_EVENT_KINDS = ['impression', 'click'] as const
export type AdEventKind = (typeof AD_EVENT_KINDS)[number]

/**
 * 리드(샘플·견적 문의)로 광고주에게 넘길 수 있는 항목. 사용자가 하나씩 체크해야 넘어간다.
 * 배합표·원가·레시피는 어떤 경우에도 넘기지 않는다
 */
export const AD_LEAD_ITEMS = ['company', 'contact', 'usage', 'store', 'region'] as const
export type AdLeadItem = (typeof AD_LEAD_ITEMS)[number]

export const AD_LEAD_STATUSES = ['active', 'withdrawn'] as const
export type AdLeadStatus = (typeof AD_LEAD_STATUSES)[number]

// ── 공통 식재료 사전 ─────────────────────────────────────────────

type Localized = Record<Language, string>

interface CommonIngredient {
  /** 화면 이름 */
  label: Localized
  /** 업소용·대용량 공급 형태 (신메뉴 식자재 정보의 '업소용 규격' 예시) */
  bulkPack: Localized
  /** 재료 이름 안에 들어 있으면 이 키로 본다. 띄어쓰기·대소문자는 무시한다 */
  aliases: readonly string[]
  /** 이 말이 들어 있으면 다른 종류다 (땅콩버터는 버터가 아니다) */
  excludes?: readonly string[]
}

/**
 * 공통 식재료 사전 — 원료 대체 광고는 같은 키끼리만 바꾼다(케첩 ↔ 케첩). 순서와 묶음은
 * recipecat-partnerships/B2B광고_매트릭스.xlsx '공통 기본 식재료' 시트(2026-10-30)를 따랐고, 시트에서 한 줄로 묶인
 * 것 중 맛·용도가 다른 것은 목업(result-buy/ingredient-ads.html)의 '같은 종류끼리' 규칙에 맞춰 나눴다
 * (간장/고추장/된장, 설탕/대체당, 식용유/참기름/들기름, 전분/밀가루, 치즈/버터/우유·크림, 케첩/토마토 페이스트, 꿀/조청).
 * 키(ketchup·butter·salt …)는 저장값이라 바꾸지 않는다. 바꾸면 등록된 광고가 짝을 잃는다
 */
export const COMMON_INGREDIENTS = {
  stock_base: {
    label: {
      ko: '육수·조미 베이스',
      en: 'Stock & seasoning base',
      ja: 'だし・調味ベース',
      zh: '高汤·调味底料',
    },
    bulkPack: {
      ko: '업소용 1~20 kg 분말·페이스트',
      en: '1~20 kg foodservice powder or paste',
      ja: '業務用 1~20 kg 粉末・ペースト',
      zh: '餐饮装 1~20 kg 粉末或膏状',
    },
    aliases: [
      '다시다',
      '육수',
      '조미료',
      '효모추출물',
      '치킨스톡',
      '굴소스',
      'stock',
      'bouillon',
      'yeast extract',
      'oyster sauce',
      'だし',
      'ブイヨン',
      'オイスターソース',
      '酵母エキス',
      '高汤',
      '鸡精',
      '蚝油',
      '酵母抽提物',
    ],
  },
  soy_sauce: {
    label: { ko: '간장', en: 'Soy sauce', ja: '醤油', zh: '酱油' },
    bulkPack: {
      ko: '업소용 13~18 L 말통',
      en: '13~18 L foodservice jug',
      ja: '業務用 13~18 L 缶',
      zh: '餐饮装 13~18 L 桶',
    },
    aliases: [
      '간장',
      'soy sauce',
      'shoyu',
      'tamari',
      '醤油',
      'しょうゆ',
      'しょう油',
      '酱油',
      '醬油',
      '生抽',
      '老抽',
    ],
  },
  gochujang: {
    label: { ko: '고추장', en: 'Gochujang', ja: 'コチュジャン', zh: '韩式辣酱' },
    bulkPack: {
      ko: '업소용 14~20 kg 통',
      en: '14~20 kg foodservice tub',
      ja: '業務用 14~20 kg 容器',
      zh: '餐饮装 14~20 kg 桶',
    },
    aliases: ['고추장', 'gochujang', 'コチュジャン', '韩式辣酱'],
  },
  doenjang: {
    label: { ko: '된장', en: 'Doenjang', ja: 'テンジャン', zh: '韩式大酱' },
    bulkPack: {
      ko: '업소용 14~20 kg 통',
      en: '14~20 kg foodservice tub',
      ja: '業務用 14~20 kg 容器',
      zh: '餐饮装 14~20 kg 桶',
    },
    aliases: ['된장', 'doenjang', 'soybean paste', 'テンジャン', '大酱', '大醬'],
  },
  sugar: {
    label: { ko: '설탕', en: 'Sugar', ja: '砂糖', zh: '白糖' },
    bulkPack: {
      ko: '업소용 15~25 kg 포대',
      en: '15~25 kg foodservice sack',
      ja: '業務用 15~25 kg 袋',
      zh: '餐饮装 15~25 kg 袋',
    },
    aliases: ['설탕', 'sugar', '砂糖', '上白糖', 'グラニュー糖', '白糖', '红糖', '紅糖'],
  },
  syrup_sweetener: {
    label: {
      ko: '올리고당·대체당',
      en: 'Syrups & sweeteners',
      ja: '水あめ・代替甘味料',
      zh: '糖浆·代糖',
    },
    bulkPack: {
      ko: '업소용 액상 말통·포대',
      en: 'Foodservice liquid jugs or sacks',
      ja: '業務用 液状缶・袋',
      zh: '餐饮装液体桶或袋装',
    },
    aliases: [
      '올리고당',
      '물엿',
      '알룰로스',
      '스테비아',
      '에리스리톨',
      'oligosaccharide',
      'corn syrup',
      'allulose',
      'stevia',
      'erythritol',
      '水あめ',
      '水飴',
      'オリゴ糖',
      'アルロース',
      'ステビア',
      '麦芽糖浆',
      '低聚糖',
      '阿洛酮糖',
      '甜菊糖',
      '赤藓糖醇',
    ],
  },
  cooking_oil: {
    label: { ko: '식용유', en: 'Cooking oil', ja: '食用油', zh: '食用油' },
    bulkPack: {
      ko: '업소용 18 L 캔',
      en: '18 L foodservice can',
      ja: '業務用 18 L 缶',
      zh: '餐饮装 18 L 桶',
    },
    aliases: [
      '식용유',
      '대두유',
      '카놀라유',
      '포도씨유',
      '해바라기유',
      '옥수수유',
      'vegetable oil',
      'canola oil',
      'soybean oil',
      'sunflower oil',
      'corn oil',
      'cooking oil',
      'サラダ油',
      '菜種油',
      '大豆油',
      'ひまわり油',
      '食用油',
      '菜籽油',
      '葵花籽油',
      '玉米油',
    ],
  },
  sesame_oil: {
    label: { ko: '참기름', en: 'Sesame oil', ja: 'ごま油', zh: '芝麻油' },
    bulkPack: {
      ko: '업소용 1.8~18 L',
      en: '1.8~18 L foodservice',
      ja: '業務用 1.8~18 L',
      zh: '餐饮装 1.8~18 L',
    },
    aliases: ['참기름', 'sesame oil', 'ごま油', '胡麻油', '香油', '芝麻油', '麻油'],
  },
  perilla_oil: {
    label: { ko: '들기름', en: 'Perilla oil', ja: 'えごま油', zh: '紫苏油' },
    bulkPack: {
      ko: '업소용 1.8~18 L',
      en: '1.8~18 L foodservice',
      ja: '業務用 1.8~18 L',
      zh: '餐饮装 1.8~18 L',
    },
    aliases: ['들기름', 'perilla oil', 'えごま油', '紫苏油', '紫蘇油'],
  },
  starch: {
    label: { ko: '전분', en: 'Starch', ja: 'でんぷん', zh: '淀粉' },
    bulkPack: {
      ko: '업소용 20~25 kg 포대',
      en: '20~25 kg foodservice sack',
      ja: '業務用 20~25 kg 袋',
      zh: '餐饮装 20~25 kg 袋',
    },
    aliases: [
      '전분',
      '녹말',
      'starch',
      'cornstarch',
      '片栗粉',
      'でんぷん',
      '澱粉',
      'コーンスターチ',
      '淀粉',
      '生粉',
    ],
  },
  wheat_flour: {
    label: { ko: '밀가루', en: 'Wheat flour', ja: '小麦粉', zh: '面粉' },
    bulkPack: {
      ko: '업소용 20 kg 포대·프리믹스',
      en: '20 kg foodservice sack or premix',
      ja: '業務用 20 kg 袋・プレミックス',
      zh: '餐饮装 20 kg 袋或预拌粉',
    },
    aliases: [
      '밀가루',
      '박력분',
      '중력분',
      '강력분',
      'flour',
      '小麦粉',
      '薄力粉',
      '強力粉',
      '中力粉',
      '面粉',
      '麵粉',
    ],
    excludes: [
      'rice flour',
      'almond flour',
      'corn flour',
      'coconut flour',
      '쌀가루',
      '아몬드가루',
      '米粉',
    ],
  },
  cheese: {
    label: { ko: '치즈', en: 'Cheese', ja: 'チーズ', zh: '奶酪' },
    bulkPack: {
      ko: '업소용 블록·슈레드 1~2.5 kg',
      en: '1~2.5 kg foodservice block or shred',
      ja: '業務用 ブロック・シュレッド 1~2.5 kg',
      zh: '餐饮装块状·丝状 1~2.5 kg',
    },
    aliases: [
      '치즈',
      '모짜렐라',
      '모차렐라',
      '체다',
      'cheese',
      'mozzarella',
      'cheddar',
      'parmesan',
      'チーズ',
      '奶酪',
      '芝士',
      '乳酪',
    ],
  },
  butter: {
    label: { ko: '버터', en: 'Butter', ja: 'バター', zh: '黄油' },
    bulkPack: {
      ko: '업소용 4.5 kg 블록·버터 시트',
      en: '4.5 kg foodservice block or sheet',
      ja: '業務用 4.5 kg ブロック・シート',
      zh: '餐饮装 4.5 kg 块状·片状',
    },
    aliases: ['버터', 'butter', 'バター', '黄油', '牛油'],
    excludes: [
      '땅콩버터',
      '쉐어버터',
      '시어버터',
      '코코아버터',
      '카카오버터',
      '버터밀크',
      'peanut butter',
      'cocoa butter',
      'shea butter',
      'buttermilk',
      'ピーナッツバター',
      'カカオバター',
      '花生酱',
      '可可脂',
    ],
  },
  milk_cream: {
    label: { ko: '우유·크림', en: 'Milk & cream', ja: '牛乳・クリーム', zh: '牛奶·奶油' },
    bulkPack: {
      ko: '업소용 1 L 팩·원유 대량',
      en: '1 L foodservice cartons or bulk',
      ja: '業務用 1 L パック・大容量',
      zh: '餐饮装 1 L 盒装或散装',
    },
    aliases: [
      '우유',
      '생크림',
      '휘핑크림',
      'milk',
      'heavy cream',
      'whipping cream',
      '牛乳',
      '生クリーム',
      '牛奶',
      '淡奶油',
      '鲜奶油',
    ],
    excludes: [
      '코코넛',
      '두유',
      '아몬드',
      '귀리',
      'coconut',
      'soy milk',
      'almond',
      'oat',
      'ココナッツ',
      '豆乳',
      '椰',
      '豆奶',
    ],
  },
  ketchup: {
    label: { ko: '케첩', en: 'Ketchup', ja: 'ケチャップ', zh: '番茄酱' },
    bulkPack: {
      ko: '업소용 2~3 kg 파우치·백인박스',
      en: '2~3 kg foodservice pouch or bag-in-box',
      ja: '業務用 2~3 kg パウチ・バッグインボックス',
      zh: '餐饮装 2~3 kg 袋装或盒中袋',
    },
    aliases: ['케첩', '케찹', 'ketchup', 'catsup', 'ケチャップ', '番茄酱', '番茄醬', '茄汁'],
  },
  tomato_paste: {
    label: { ko: '토마토 페이스트', en: 'Tomato paste', ja: 'トマトペースト', zh: '番茄膏' },
    bulkPack: {
      ko: '업소용 3 kg 캔·드럼',
      en: '3 kg foodservice can or drum',
      ja: '業務用 3 kg 缶・ドラム',
      zh: '餐饮装 3 kg 罐或桶',
    },
    aliases: ['토마토페이스트', 'tomato paste', 'トマトペースト', '番茄膏'],
  },
  mayonnaise: {
    label: { ko: '마요네즈', en: 'Mayonnaise', ja: 'マヨネーズ', zh: '蛋黄酱' },
    bulkPack: {
      ko: '업소용 3.2 kg 짤주머니·백인박스',
      en: '3.2 kg foodservice piping bag or bag-in-box',
      ja: '業務用 3.2 kg 絞り袋・バッグインボックス',
      zh: '餐饮装 3.2 kg 挤袋或盒中袋',
    },
    aliases: [
      '마요네즈',
      '마요',
      'mayonnaise',
      'mayo',
      'マヨネーズ',
      '蛋黄酱',
      '蛋黃醬',
      '美乃滋',
      '沙拉酱',
    ],
  },
  salt: {
    label: { ko: '소금', en: 'Salt', ja: '塩', zh: '盐' },
    bulkPack: {
      ko: '업소용 20 kg 포대',
      en: '20 kg foodservice sack',
      ja: '業務用 20 kg 袋',
      zh: '餐饮装 20 kg 袋',
    },
    aliases: ['소금', '천일염', '정제염', '죽염', 'salt', '塩', '食塩', '盐', '鹽'],
    excludes: ['unsalted', 'salted', '塩麹', '塩辛', '塩昆布', '盐焗'],
  },
  chili_powder: {
    label: { ko: '고춧가루', en: 'Chili powder', ja: '粉唐辛子', zh: '辣椒粉' },
    bulkPack: {
      ko: '업소용 1~20 kg',
      en: '1~20 kg foodservice',
      ja: '業務用 1~20 kg',
      zh: '餐饮装 1~20 kg',
    },
    aliases: [
      '고춧가루',
      '고추가루',
      'gochugaru',
      'chili powder',
      'chili flakes',
      'red pepper flakes',
      '粉唐辛子',
      '一味唐辛子',
      '辣椒粉',
      '辣椒面',
    ],
  },
  garlic: {
    label: { ko: '마늘', en: 'Garlic', ja: 'にんにく', zh: '大蒜' },
    bulkPack: {
      ko: '업소용 1 kg 냉동 다진마늘·분말',
      en: '1 kg frozen minced or powder',
      ja: '業務用 1 kg 冷凍刻み・粉末',
      zh: '餐饮装 1 kg 冷冻蒜蓉或蒜粉',
    },
    aliases: ['마늘', 'garlic', 'にんにく', 'ニンニク', '大蒜', '蒜'],
  },
  ginger: {
    label: { ko: '생강', en: 'Ginger', ja: '生姜', zh: '生姜' },
    bulkPack: {
      ko: '업소용 1 kg 냉동 다진생강·분말',
      en: '1 kg frozen minced or powder',
      ja: '業務用 1 kg 冷凍刻み・粉末',
      zh: '餐饮装 1 kg 冷冻姜末或姜粉',
    },
    aliases: ['생강', 'ginger', '生姜', 'しょうが', 'ショウガ', '姜'],
  },
  vinegar: {
    label: { ko: '식초', en: 'Vinegar', ja: '酢', zh: '醋' },
    bulkPack: {
      ko: '업소용 15~18 L 말통',
      en: '15~18 L foodservice jug',
      ja: '業務用 15~18 L 缶',
      zh: '餐饮装 15~18 L 桶',
    },
    aliases: ['식초', 'vinegar', '酢', '醋'],
  },
  egg: {
    label: { ko: '달걀', en: 'Egg', ja: '卵', zh: '鸡蛋' },
    bulkPack: {
      ko: '살균 액란·난황 업소용 팩',
      en: 'Pasteurized liquid egg packs',
      ja: '業務用 殺菌液卵パック',
      zh: '餐饮装杀菌液蛋',
    },
    aliases: [
      '달걀',
      '계란',
      '액란',
      '난황',
      '전란',
      'egg',
      '卵',
      'たまご',
      '玉子',
      '鸡蛋',
      '雞蛋',
      '蛋黄',
      '蛋液',
    ],
    excludes: ['eggplant', '茄子'],
  },
  honey: {
    label: { ko: '꿀', en: 'Honey', ja: 'はちみつ', zh: '蜂蜜' },
    bulkPack: {
      ko: '업소용 드럼·말통',
      en: 'Foodservice drum or jug',
      ja: '業務用 ドラム・缶',
      zh: '餐饮装桶装',
    },
    aliases: ['꿀', 'honey', 'はちみつ', '蜂蜜'],
  },
  grain_syrup: {
    label: { ko: '조청', en: 'Rice syrup', ja: '米あめ', zh: '米糖浆' },
    bulkPack: {
      ko: '업소용 드럼·말통',
      en: 'Foodservice drum or jug',
      ja: '業務用 ドラム・缶',
      zh: '餐饮装桶装',
    },
    aliases: ['조청', 'rice syrup', 'grain syrup', '米あめ', '米糖浆'],
  },
} as const satisfies Record<string, CommonIngredient>

export type CommonIngredientKey = keyof typeof COMMON_INGREDIENTS
export const COMMON_INGREDIENT_KEYS = Object.keys(COMMON_INGREDIENTS) as [
  CommonIngredientKey,
  ...CommonIngredientKey[],
]

const squash = (text: string) => text.normalize('NFKC').toLowerCase().replace(/\s+/g, '')

/**
 * 재료 이름 → 공통 식재료 키. 별칭이 이름 안에 들어 있으면 그 키이고, 여럿이면 가장 긴 별칭이 이긴다
 * ('蛋黄酱' 은 달걀이 아니라 마요네즈, 'unsalted butter' 는 소금이 아니라 버터). 모르면 null
 */
export function commonIngredientKey(name: string): CommonIngredientKey | null {
  const plain = squash(name)
  if (!plain) return null
  let best: { key: CommonIngredientKey; length: number } | null = null
  for (const key of COMMON_INGREDIENT_KEYS) {
    const entry: CommonIngredient = COMMON_INGREDIENTS[key]
    if (entry.excludes?.some((word) => plain.includes(squash(word)))) continue
    for (const alias of entry.aliases) {
      const a = squash(alias)
      if (plain.includes(a) && (!best || a.length > best.length)) best = { key, length: a.length }
    }
  }
  return best?.key ?? null
}

// ── 광고 카드 입력 (관리자) ───────────────────────────────────────

const shortText = (max: number) => z.string().trim().max(max).default('')
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')

/** 광고주. contractConfirmed 를 켜야 그 회사의 광고를 승인할 수 있다 (계약 전 실명 노출 금지) */
export const adAdvertiserInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  contactName: shortText(60),
  contactEmail: z.union([z.literal(''), z.email().max(120)]).default(''),
  contactPhone: shortText(40),
  /** 광고 계약(회사명·상표 표기 허락 포함)을 확인했는지 */
  contractConfirmed: z.boolean().default(false),
  memo: shortText(500),
})
export type AdAdvertiserInput = z.infer<typeof adAdvertiserInputSchema>

/** 카드에 보이는 글과 숫자. 회사가 낸 원재료 규격서·견적서 기준으로 관리자가 넣는다 */
export const adContentSchema = z.object({
  /** 광고 원료·용기·서비스 이름, 예 '업소용 케첩 2 kg' */
  productName: z.string().trim().min(1).max(80),
  /** 한 줄 설명 */
  body: shortText(160),
  /** 업소용 규격, 예 '2 kg 파우치' */
  specText: shortText(60),
  /** 단가 (부가세 별도). 원료 대체·포장재는 있어야 원가 차이를 계산한다 */
  priceAmount: z.number().nonnegative().max(1e9).nullable().default(null),
  /** 단가 기준 단위 — 재료 단가 캐시와 같은 kg·L·ea */
  priceUnit: z.enum(PRICE_BASE_UNITS).nullable().default(null),
  minOrderText: shortText(40),
  sampleText: shortText(40),
  deliveryText: shortText(40),
  /** 원재료 표기 문구(복합원재료 괄호 속 성분, 많이 넣은 순) */
  labelText: shortText(300),
  originText: shortText(60),
  /** 맛 예측 메모, 예 '단맛이 조금 줄어요' */
  tasteNote: shortText(60),
})
export type AdContent = z.infer<typeof adContentSchema>

export const adCampaignInputSchema = z
  .object({
    advertiserId: z.string().min(1).max(60),
    placement: z.enum(AD_PLACEMENTS),
    offerKind: z.enum(AD_OFFER_KINDS),
    market: z.enum(MARKETS),
    /** null = 두 축 모두. 생성 대기 광고는 식품(product)·메뉴(menu)를 나눠 고른다 */
    axis: z.enum(AXES).nullable().default(null),
    /** 노출 대분류(taxonomy majorId). 비우면 그 축 전체 */
    majorIds: z.array(z.string().min(1).max(40)).max(40).default([]),
    /** 원료 대체·업소용 받는 곳: 같은 공통 식재료 키 재료에만 붙는다 */
    ingredientKey: z.enum(COMMON_INGREDIENT_KEYS).nullable().default(null),
    content: adContentSchema,
    /** 문의(리드)를 받을 때 사용자가 체크해 넘길 항목. 비우면 문의 버튼이 없다 */
    leadItems: z.array(z.enum(AD_LEAD_ITEMS)).max(AD_LEAD_ITEMS.length).default([]),
    billingUnit: z.enum(AD_BILLING_UNITS),
    /** 과금 단위 하나의 금액 (per_mille 은 1,000회 노출당, fixed_slot 은 월 고정) */
    billingAmount: z.number().nonnegative().max(1e9),
    /** 월 예산. 다 쓰면 그달은 나가지 않는다. fixed_slot 은 쓰지 않는다 */
    monthlyBudget: z.number().nonnegative().max(1e10),
    startsOn: day,
    endsOn: day.nullable().default(null),
  })
  .refine((v) => !v.endsOn || v.endsOn >= v.startsOn, {
    message: '끝나는 날이 시작일보다 빠를 수 없습니다',
    path: ['endsOn'],
  })
  .refine((v) => new Set(v.leadItems).size === v.leadItems.length, { path: ['leadItems'] })
export type AdCampaignInput = z.infer<typeof adCampaignInputSchema>

/** 관리자가 바꾸는 상태. approved 는 문구 검사를 통과해야 한다 (서버가 다시 확인) */
export const adStatusChangeSchema = z.object({
  status: z.enum(['pending_review', 'approved', 'paused', 'rejected']),
})

// ── 문구 검사 (승인 전) ──────────────────────────────────────────

export type AdCopyProblem =
  | { code: 'health_claim'; field: keyof AdContent; matches: string[] }
  | { code: 'contract_unconfirmed' }
  | { code: 'price_missing' }
  | { code: 'ingredient_key_missing' }
  | { code: 'lead_items_missing' }

/** 효능·질병 표현을 찾는 글 칸 — 사용자에게 보이는 글 전부 */
const COPY_FIELDS = [
  'productName',
  'body',
  'specText',
  'minOrderText',
  'sampleText',
  'deliveryText',
  'labelText',
  'originText',
  'tasteNote',
] as const satisfies readonly (keyof AdContent)[]

/**
 * 승인을 막는 문제 목록 (비면 승인 가능). 효능·질병 표현은 생성 결과의 판매 문구를 거르는 같은 목록
 * (health-claims.ts, 식품표시광고법 제8조, 로드맵 P8)으로 찾는다. 여기서는 고치지 않고 막기만 한다 — 광고 문구는
 * 회사가 낸 글이라 바꿔 내보내면 안 된다
 */
export function adCopyProblems(
  campaign: Pick<
    AdCampaignInput,
    'placement' | 'ingredientKey' | 'content' | 'leadItems' | 'billingUnit'
  >,
  advertiser: { contractConfirmed: boolean },
): AdCopyProblem[] {
  const problems: AdCopyProblem[] = []
  for (const field of COPY_FIELDS) {
    const matches = findHealthClaims(campaign.content[field])
    if (matches.length) problems.push({ code: 'health_claim', field, matches })
  }
  if (!advertiser.contractConfirmed) problems.push({ code: 'contract_unconfirmed' })
  const needsPrice = campaign.placement === 'ingredient_swap' || campaign.placement === 'packaging'
  if (needsPrice && (campaign.content.priceAmount === null || campaign.content.priceUnit === null))
    problems.push({ code: 'price_missing' })
  if (INGREDIENT_AD_PLACEMENTS.includes(campaign.placement) && !campaign.ingredientKey)
    problems.push({ code: 'ingredient_key_missing' })
  if (campaign.billingUnit === 'per_lead' && campaign.leadItems.length === 0)
    problems.push({ code: 'lead_items_missing' })
  return problems
}

// ── 앱이 받는 광고 ───────────────────────────────────────────────

/** 앱에 나가는 광고 한 장. 과금·예산·광고주 연락처는 싣지 않는다 */
export interface AdCardView {
  id: string
  placement: AdPlacement
  offerKind: AdOfferKind
  advertiserName: string
  content: AdContent
  currency: Currency
  ingredientKey: CommonIngredientKey | null
  /** 재료 줄 자리면 붙일 재료 id, 그 밖엔 null */
  ingredientId: string | null
  leadItems: AdLeadItem[]
  /** 이 사용자가 이 광고에 낸 살아 있는 문의. 있으면 앱은 '요청함 · 철회'를 보인다 */
  myLeadId: string | null
}

export const adPickRequestSchema = z.object({
  placement: z.enum(AD_PLACEMENTS),
  simulationId: z.string().min(1).max(60),
})

export const adClickSchema = z.object({ simulationId: z.string().min(1).max(60) })

/** 문의 한 건. consent 는 화면의 '동의하고 요청'이 true 로만 보낸다 */
export const adLeadRequestSchema = z.object({
  simulationId: z.string().min(1).max(60),
  consent: z.literal(true),
  items: z
    .object({
      company: z.string().trim().min(1).max(60),
      contact: z.string().trim().min(3).max(120),
      usage: z.string().trim().min(1).max(60),
      store: z.string().trim().min(1).max(60),
      region: z.string().trim().min(1).max(40),
    })
    .partial(),
})
export type AdLeadRequest = z.infer<typeof adLeadRequestSchema>

/** 광고가 요구한 항목이 다 있으면 그 항목만 남긴 값, 하나라도 빠지면 null — 요구하지 않은 값은 받아도 버린다 */
export function adLeadFields(
  required: readonly AdLeadItem[],
  items: AdLeadRequest['items'],
): Partial<Record<AdLeadItem, string>> | null {
  if (required.length === 0) return null
  const out: Partial<Record<AdLeadItem, string>> = {}
  for (const item of required) {
    const value = items[item]
    if (!value) return null
    out[item] = value
  }
  return out
}

// ── 광고 고르기 (단일 원본) ──────────────────────────────────────

export const AD_VIEWER_PLANS = ['guest', 'free', 'basic', 'pro'] as const
export type AdViewerPlan = (typeof AD_VIEWER_PLANS)[number]

export interface AdCandidate {
  id: string
  advertiserId: string
  status: AdStatus
  placement: AdPlacement
  market: Market
  axis: Axis | null
  majorIds: readonly string[]
  ingredientKey: CommonIngredientKey | null
  startsOn: string
  endsOn: string | null
  content: Pick<AdContent, 'priceAmount' | 'priceUnit'>
  /** 이번 달 쓴 돈 ÷ 월 예산 (fixed_slot 은 0). 1 이상이면 예산 소진 */
  spentRatio: number
}

export interface AdPickIngredient {
  id: string
  name: string
  unit: string
  /** 배합 비율 % (모르면 null) */
  ratio: number | null
}

/** 재료 단가 캐시의 시세 (kg·L 당). 모르면 그 재료의 단가 확인을 건너뛴다 */
export interface AdMarketPrice {
  unit: PriceBaseUnit
  low: number
  high: number
}

export interface AdPickInput {
  placement: AdPlacement
  market: Market
  axis: Axis
  majorId: string | null
  plan: AdViewerPlan
  /** 한국 날짜 YYYY-MM-DD */
  today: string
  ingredients: readonly AdPickIngredient[]
  /** 사용자가 고정한 재료 id — 광고를 붙이지 않는다 */
  lockedIngredientIds: readonly string[]
  /** 재료 id → 시세. 광고 단가가 이 범위를 크게 벗어나면 내보내지 않는다 */
  marketPrices: Readonly<Record<string, AdMarketPrice | null>>
  viewer: {
    /** 오늘 이 자리에서 이 사람에게 이미 센 노출 수 (다른 제품 포함) */
    placementShownToday: number
    /** 광고 id → 오늘 이 사람에게 센 노출 수 */
    adShownToday: Readonly<Record<string, number>>
    /** 같은 제품·같은 날 이미 보여 준 광고 — 다시 열어도 상한에 다시 세지 않고 그대로 보인다 */
    shownHere: readonly string[]
    /** 이 제품 전에 받은 결과(다 끝난 생성) 수. 0 이면 지금이 첫 생성이다 — 생성 대기 광고를 내보내지 않는다 */
    resultsBefore: number
  }
  settings: B2bAdsLimits
}

/** 관리자 설정 중 고르기에 쓰는 값 (settings.ts 의 b2bAds) */
export interface B2bAdsLimits {
  perScreenMax: number
  dailyCapPerViewer: number
  dailyCapPerAd: number
  proSeesResultAds: boolean
  proSeesWaitAds: boolean
}

export interface AdPick {
  adId: string
  ingredientId: string | null
}

/** 배합 비율이 이보다 낮은 재료에는 원료 대체 광고를 붙이지 않는다. 소금은 비율이 낮아도 표기 변화가 커서 예외 */
export const AD_MIN_INGREDIENT_RATIO = 1
/** 광고 단가가 시세 아래로 이 배 미만이거나 위로 이 배 넘게 벗어나면 내보내지 않는다 */
export const AD_PRICE_BAND = 2

/** 광고 단가(기준 단위)가 시세 범위 안쪽(앞뒤로 AD_PRICE_BAND 배까지)인지. 시세나 단가를 모르면 true */
export function adPriceWithinMarket(
  price: Pick<AdContent, 'priceAmount' | 'priceUnit'>,
  market: AdMarketPrice | null | undefined,
): boolean {
  if (!market || price.priceAmount === null || price.priceUnit !== market.unit) return true
  return (
    price.priceAmount >= market.low / AD_PRICE_BAND &&
    price.priceAmount <= market.high * AD_PRICE_BAND
  )
}

/**
 * 한 화면에 보일 광고를 고른다. 서버만 부르고(routes/ads.ts), 결과 데이터나 AI 대체안 순서에는 손대지 않는다.
 * 0) 생성 대기 광고는 손님과 첫 생성(이전 결과 0개)에게 내보내지 않는다. 첫 결과는 약 15초라 광고를 읽을 틈이 없고,
 *    광고 문의 창이 첫 결과를 가리면 안 된다 (첫 결과 이탈 분석 2026-10-30 권고 5)
 * 1) Pro: 관리자 설정(proSeesResultAds·proSeesWaitAds)이 끄면 빈 목록 (대표 미정 — 기본은 대기 광고만 숨김)
 * 2) 승인·시장·기간·예산·축·대분류가 맞는 광고만
 * 3) 재료 줄 자리는 같은 공통 식재료 키의 재료에만, 고정한 재료·비율 1% 미만(소금 제외)·시세 밖 단가는 뺀다
 * 4) 하루 상한: 같은 사람에게 이 자리 dailyCapPerViewer 번, 같은 광고 dailyCapPerAd 번 (같은 제품에서 다시 보는 것은 세지 않는다)
 * 5) 광고주 하나·재료 하나에 한 장, 화면당 perScreenMax(1~2) 장. 순서는 예산을 덜 쓴 광고부터, 같으면 id — 돈을 더 낸다고 앞서지 않는다
 */
export function pickAds(candidates: readonly AdCandidate[], input: AdPickInput): AdPick[] {
  const { settings, viewer } = input
  if (
    input.placement === 'generation_wait' &&
    (input.plan === 'guest' || viewer.resultsBefore === 0)
  ) {
    return []
  }
  if (input.plan === 'pro') {
    const hidden =
      input.placement === 'generation_wait' ? !settings.proSeesWaitAds : !settings.proSeesResultAds
    if (hidden) return []
  }
  const perScreen = Math.max(1, Math.min(2, Math.floor(settings.perScreenMax)))
  const locked = new Set(input.lockedIngredientIds)
  const shownHere = new Set(viewer.shownHere)
  let freshLeft = Math.max(0, settings.dailyCapPerViewer - viewer.placementShownToday)

  const eligible = candidates
    .filter(
      (ad) =>
        ad.status === 'approved' &&
        ad.placement === input.placement &&
        ad.market === input.market &&
        ad.startsOn <= input.today &&
        (ad.endsOn === null || ad.endsOn >= input.today) &&
        ad.spentRatio < 1 &&
        (ad.axis === null || ad.axis === input.axis) &&
        (ad.majorIds.length === 0 ||
          (input.majorId !== null && ad.majorIds.includes(input.majorId))),
    )
    .sort((a, b) => a.spentRatio - b.spentRatio || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))

  const picks: AdPick[] = []
  const usedAdvertisers = new Set<string>()
  const usedIngredients = new Set<string>()
  for (const ad of eligible) {
    if (picks.length >= perScreen) break
    if (usedAdvertisers.has(ad.advertiserId)) continue
    const again = shownHere.has(ad.id)
    if (!again && (freshLeft <= 0 || (viewer.adShownToday[ad.id] ?? 0) >= settings.dailyCapPerAd))
      continue
    let ingredientId: string | null = null
    if (INGREDIENT_AD_PLACEMENTS.includes(ad.placement)) {
      const target = input.ingredients.find(
        (ing) =>
          !locked.has(ing.id) &&
          !usedIngredients.has(ing.id) &&
          commonIngredientKey(ing.name) === ad.ingredientKey &&
          (ad.ingredientKey === 'salt' ||
            ing.ratio === null ||
            ing.ratio >= AD_MIN_INGREDIENT_RATIO) &&
          (ad.placement !== 'ingredient_swap' ||
            (unitFactorOrNull(ing.unit, ad.content.priceUnit) !== null &&
              adPriceWithinMarket(ad.content, input.marketPrices[ing.id]))),
      )
      if (!target) continue
      ingredientId = target.id
      usedIngredients.add(target.id)
    }
    usedAdvertisers.add(ad.advertiserId)
    if (!again) freshLeft -= 1
    picks.push({ adId: ad.id, ingredientId })
  }
  return picks
}

function unitFactorOrNull(ingredientUnit: string, priceUnit: PriceBaseUnit | null): number | null {
  return priceUnit === null ? null : unitFactor(ingredientUnit, priceUnit)
}

/**
 * 광고 원료로 바꾸면 그 재료의 새 단가(재료 qty 단위당). 단위가 안 맞거나 단가가 없으면 null.
 * 앱의 '바꿔 보기'가 이 값을 기존 재료 바꾸기 흐름(초안 → 변경 저장/되돌리기)에 넣는다
 */
export function adSwapUnitPrice(
  ingredientUnit: string,
  content: Pick<AdContent, 'priceAmount' | 'priceUnit'>,
): number | null {
  if (content.priceAmount === null) return null
  const factor = unitFactorOrNull(ingredientUnit, content.priceUnit)
  return factor === null ? null : content.priceAmount * factor
}

// ── 과금 계산 ────────────────────────────────────────────────────

/** 한 달 기록으로 쓴 돈. fixed_slot 은 월 고정이라 노출과 무관하게 billingAmount */
export function adSpend(
  billing: { billingUnit: AdBillingUnit; billingAmount: number },
  counts: { impressions: number; clicks: number; leads: number },
): number {
  switch (billing.billingUnit) {
    case 'per_lead':
      return counts.leads * billing.billingAmount
    case 'per_click':
      return counts.clicks * billing.billingAmount
    case 'per_mille':
      return (counts.impressions * billing.billingAmount) / 1000
    case 'fixed_slot':
      return billing.billingAmount
  }
}

/** 예산 소진 비율. fixed_slot 은 예산을 쓰지 않으므로 0, 예산 0 이면 다 쓴 것으로 본다 */
export function adSpentRatio(
  billing: { billingUnit: AdBillingUnit; billingAmount: number; monthlyBudget: number },
  counts: { impressions: number; clicks: number; leads: number },
): number {
  if (billing.billingUnit === 'fixed_slot') return 0
  if (billing.monthlyBudget <= 0) return 1
  return adSpend(billing, counts) / billing.monthlyBudget
}

/** 노출·하루 상한·월 예산의 날짜 기준은 한국 시간 */
export function adDayKst(at: Date): string {
  return new Date(at.getTime() + 9 * 3600_000).toISOString().slice(0, 10)
}

// ── 관리자 화면 ──────────────────────────────────────────────────

export interface AdAdvertiserView extends AdAdvertiserInput {
  id: string
  createdAt: string
}

export interface AdCampaignAdminView extends AdCampaignInput {
  id: string
  status: AdStatus
  reviewedBy: string | null
  reviewedAt: string | null
  updatedAt: string
  /** 이번 달(한국 시간) 기록 */
  month: { impressions: number; clicks: number; leads: number; spend: number }
  /** 승인을 막는 문제 (비면 승인 가능) */
  problems: AdCopyProblem[]
}

export interface AdLeadAdminView {
  id: string
  campaignId: string
  advertiserId: string
  simulationId: string
  status: AdLeadStatus
  fields: Partial<Record<AdLeadItem, string>>
  createdAt: string
  withdrawnAt: string | null
}
